/**
 * LibraryCatalog — каталог общего контента из библиотеки (И7 + И4).
 *
 * Грузит type='library' игры (GET /api/games?type=library[&category=&lang=]) и для
 * каждой предлагает:
 *   - «Начать сессию» — провести квиз как есть (onStart(gameId), тот же флоу, что у
 *     пользовательских игр);
 *   - «Клонировать в черновик» — мост «библиотечный квиз → черновик редактора»:
 *     GET /api/games/{id} → wire-формат → wireQuizToDraft → saveDraft (localStorage),
 *     затем переход в /edit для правки.
 *
 * Параметризуется категорией (И4): category='showcase' → «Открытая витрина»
 * (тематические квизы), 'questions' → «Библиотека вопросов» (примеры по типам).
 * Размещается в HostSelect двумя секциями. Не зависит от WS — это выбор шаблона до
 * старта сессии.
 *
 * library-i18n (v14): секция questions дополнительно фильтруется по языку шаблонов
 * (?lang=); showcase и общий листинг остаются языконейтральными (lang не передаётся).
 * Дефолт языка — живая локаль i18n (0.14.2: смена языка на лету рефечит секцию
 * с новым ?lang=; прежде initialLocale замораживался на старт приложения, т.к.
 * смена языка была перезагрузкой на поддомен). Фолбэк initialLocale — для
 * SSR/старта до инициализации i18n.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getGame,
  listLibraryGames,
  parseWireQuizPayload,
  wireQuizToDraft,
  type GameMeta,
} from '../api';
import { saveDraft } from '../drafts';
import { initialLocale } from '../i18n';
import { SUPPORTED_LOCALES } from '../i18n/locale';
import { errorMessage } from '../util';

export interface LibraryCatalogProps {
  /** Старт сессии по выбранному библиотечному квизу. */
  onStart: (gameId: number) => void;
  /** После успешного клонирования — переход (обычно navigate('/edit')). */
  onCloned?: (gameId: number) => void;
  /** Блокировать кнопки, пока родитель стартует сессию. */
  starting?: boolean;
  /** Категория library (И4): 'showcase' | 'questions'. Без — весь library. */
  category?: string;
  /** Язык шаблонов секции questions (library-i18n, v14): 'ru' | 'en' | 'cn'. Проп —
   * явность/тесты; по умолчанию — текущая локаль i18n (реагирует на смену языка
   * на лету, 0.14.2; фолбэк initialLocale). Применяется только при
   * category='questions' — showcase языконейтрален. */
  lang?: string;
  /** Заголовок секции (уже переведённый). Без — t('library.title'). */
  title?: string;
  /** data-testid корневого контейнера (для тестов различения секций). */
  testId?: string;
}

export function LibraryCatalog({
  onStart,
  onCloned,
  starting = false,
  category,
  lang,
  title,
  testId = 'library-catalog',
}: LibraryCatalogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [games, setGames] = useState<GameMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cloningId, setCloningId] = useState<number | null>(null);

  // Живая локаль i18n (0.14.2): после смены языка на лету секция questions
  // рефетчится с новым ?lang=. До инициализации/SSR — фолбэк initialLocale.
  const liveLang = (SUPPORTED_LOCALES as readonly string[]).includes(i18n.resolvedLanguage ?? '')
    ? (i18n.resolvedLanguage as string)
    : null;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // lang-фильтр — только для «Библиотеки вопросов»: showcase/общий листинг
      // языконейтральны, параметр не ставится (сервер его бы всё равно проигнорировал).
      const effectiveLang =
        category === 'questions' ? (lang ?? liveLang ?? initialLocale) : undefined;
      setGames(await listLibraryGames(category, { lang: effectiveLang }));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [category, lang, liveLang]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleClone(g: GameMeta): Promise<void> {
    setCloningId(g.id);
    setError(null);
    try {
      const full = await getGame(g.id);
      const wire = parseWireQuizPayload(full.payload);
      const draft = wireQuizToDraft(wire, full.title);
      saveDraft(draft);
      onCloned?.(g.id);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setCloningId(null);
    }
  }

  return (
    <div className="library" data-testid={testId}>
      <div className="block-head">
        <span className="block-subtitle">{title ?? t('library.title')}</span>
        <button type="button" className="link-btn" onClick={() => void refresh()}>
          {t('common.refresh')}
        </button>
      </div>

      {loading ? (
        <p className="muted">{t('library.loading')}</p>
      ) : error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : games.length === 0 ? (
        <p className="muted">{t('library.empty')}</p>
      ) : (
        <ul className="library-grid" aria-label={t('library.listAria')}>
          {games.map((g) => (
            <li key={g.id} className="library-card" data-testid="library-card">
              <h4 className="library-card__title">{g.title}</h4>
              <div className="library-card__actions">
                <button
                  type="button"
                  className="btn"
                  disabled={starting || cloningId !== null}
                  onClick={() => onStart(g.id)}
                >
                  {t('library.startSession')}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={starting || cloningId !== null}
                  onClick={() => void handleClone(g)}
                >
                  {cloningId === g.id ? t('library.cloning') : t('library.clone')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
